import React, { useState } from 'react';

import { TextArea } from '../../../shared-components/TextArea/TextArea';
import styles from './OfflineMode.module.scss';
import { BROADCAST } from './copy';
import { formatCount } from './reachReading';
import type {
  OfflineModeBroadcastPrepare,
  OfflineModeBroadcastRefusal,
  OfflineModeBroadcastSendInput,
  OfflineModeBroadcastTemplate,
} from 'redux-stores/offlineMode';

type TemplateKey = 'activation' | 'return';

export type OfflineModeBroadcastComposeProps = {
  organizationName: string;
  prepare: OfflineModeBroadcastPrepare;
  sending: boolean;
  refusal: OfflineModeBroadcastRefusal | null;
  onSend: (input: OfflineModeBroadcastSendInput) => void;
  onCancel: () => void;
};

const BODY_FIELD_ID = 'offlineModeBroadcastBodyField';

const TEMPLATE_CHOICES: { key: TemplateKey; label: string }[] = [
  { key: 'activation', label: BROADCAST.templateActivation },
  { key: 'return', label: BROADCAST.templateReturn },
];

export default function OfflineModeBroadcastCompose({
  organizationName,
  prepare,
  sending,
  refusal,
  onSend,
  onCancel,
}: OfflineModeBroadcastComposeProps) {
  const [template, setTemplate] = useState<TemplateKey>(
    prepare.defaultTemplate === 'return' ? 'return' : 'activation'
  );
  const [body, setBody] = useState<string>(prepare.templates[template]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [pendingTemplate, setPendingTemplate] = useState<TemplateKey | null>(null);

  const { resolvedCount } = prepare.recipientScope;
  const { reachableNow } = prepare;

  const edited = body !== prepare.templates[template];
  const alert = fieldError ?? (refusal ? BROADCAST.refusal[refusal.code](refusal.detail) : null);

  const applyTemplate = (key: TemplateKey) => {
    setTemplate(key);
    setBody(prepare.templates[key]);
    setPendingTemplate(null);
    setFieldError(null);
  };

  const chooseTemplate = (key: TemplateKey) => {
    if (key === template && !edited) return;
    if (edited) {
      setPendingTemplate(key);
      return;
    }
    applyTemplate(key);
  };

  const handleSend = () => {
    if (!body.trim()) {
      setFieldError(BROADCAST.emptyBody);
      return;
    }
    if (new Blob([body]).size > prepare.limits.maxBodyBytes) {
      setFieldError(BROADCAST.refusal.body_too_large());
      return;
    }
    onSend({
      body,
      template: (edited ? 'custom' : template) as OfflineModeBroadcastTemplate,
    });
  };

  return (
    <section className={styles.broadcast} data-test-id="offlineModeBroadcastCompose">
      <h3 className={styles.broadcastHeading}>
        {BROADCAST.title(prepare.direction, organizationName)}
      </h3>

      <p className={styles.broadcastLine}>
        {BROADCAST.recipientsLine(organizationName, formatCount(resolvedCount))}
      </p>

      <p className={styles.broadcastLine}>
        {reachableNow
          ? BROADCAST.reachableLine(formatCount(reachableNow.count))
          : BROADCAST.reachableUnknown}
      </p>

      {resolvedCount === 0 ? (
        <p className={styles.broadcastLine} data-test-id="offlineModeBroadcastZeroRecipients">
          {BROADCAST.zeroRecipients}
        </p>
      ) : null}

      <div className={styles.broadcastTemplates} data-test-id="offlineModeBroadcastTemplate">
        {TEMPLATE_CHOICES.map((choice) => (
          <button
            aria-pressed={choice.key === template}
            className={styles.broadcastTemplate}
            data-test-id={`offlineModeBroadcastTemplate-${choice.key}`}
            key={choice.key}
            onClick={() => chooseTemplate(choice.key)}
            type="button"
          >
            {choice.label}
          </button>
        ))}
      </div>

      {pendingTemplate ? (
        <div
          className={styles.broadcastReplaceEdits}
          data-test-id="offlineModeBroadcastReplaceEdits"
        >
          <span className={styles.broadcastReplaceEditsPrompt}>{BROADCAST.replaceEditsPrompt}</span>
          <button
            className={styles.secondaryAction}
            onClick={() => applyTemplate(pendingTemplate)}
            type="button"
          >
            {BROADCAST.replaceEditsConfirm}
          </button>
          <button
            className={styles.tertiaryAction}
            onClick={() => setPendingTemplate(null)}
            type="button"
          >
            {BROADCAST.replaceEditsKeep}
          </button>
        </div>
      ) : null}

      <label className={styles.fieldLabel} htmlFor={BODY_FIELD_ID}>
        {BROADCAST.bodyLabel}
      </label>

      <TextArea
        dataTestId="offlineModeBroadcastBody"
        id={BODY_FIELD_ID}
        disabled={sending}
        onTextAreaChange={(event) => {
          setBody(event.target.value);
          setFieldError(null);
        }}
        rows={6}
        value={body}
      />

      {alert ? (
        <span className={styles.fieldError} data-test-id="offlineModeBroadcastError" role="alert">
          {alert}
        </span>
      ) : null}

      <div className={styles.broadcastActions}>
        <button
          className={styles.primaryAction}
          data-test-id="offlineModeBroadcastSend"
          disabled={sending || resolvedCount === 0}
          onClick={handleSend}
          type="button"
        >
          {sending ? BROADCAST.sending : BROADCAST.send}
        </button>
        <button
          className={styles.secondaryAction}
          data-test-id="offlineModeBroadcastCancel"
          onClick={onCancel}
          type="button"
        >
          {BROADCAST.cancel}
        </button>
      </div>
    </section>
  );
}
